import { useAuth } from 'contexts/auth';
import { createContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import userRequests from 'services/user';
import theme from 'shell/themes';

const defaultForm = {
    firstName: '',
    lastName: '',
    preferredName: '',
    password: '',
    confirmPassword: '',
};

const PAGES = {
    NAME: 'enter-name',
    NEW_PASSWORD: 'new-password',
    DETAILS: 'details',
};

const PageContext = createContext();

const PageProvider = (props) => {
    const history = useHistory();
    const { auth, updateCurrentUser, updatePassword } = useAuth();

    const [loading, setLoading] = useState(false); // will add loading state soon
    const [form, setForm] = useState(defaultForm);
    const [errors, setErrors] = useState({
        'enter-name': {
            firstName: '',
            lastName: '',
            preferredName: '',
        },
        'new-password': {
            password: '',
            confirmPassword: '',
        },
        details: {},
    });
    const [currentStep, setCurrentStep] = useState(PAGES.NAME);
    const [satisfyRequirement, setSatisfyRequirement] = useState({
        validateMoreThan8Characters: true,
        validateOneUpperCase: true,
        validateOneLowerCase: true,
        validateOneNumber: true,
        validateOneSpecial: true,
    });

    const handlePrev = (step) => {
        return setCurrentStep(step);
    };

    const handleNext = (step) => {
        if (!validateAll()) return;
        setCurrentStep(step);
    };

    const handleComplete = async ({ skip = false }) => {
        if (!validateAll()) return;
        setLoading(true);
        let updateUserObj = {
            display_name: form.preferredName || '',
            first_name: form.firstName,
            last_name: form.lastName,
            profile_circle: {
                bg_color: theme.palette.ensightful.error[800],
                text_color: theme.palette.ensightful.shades[0],
            },
            profile_set_up: true,
        };
        if (!skip) {
            updateUserObj = {
                ...updateUserObj,
                pronoun: form.pronoun?.key || '',
                country: form.country?.key || '',
                timezone: form.timezone?.key || '',
                english_proficiency: form.englishProficiency?.key || '',
            };
        }
        const getUser = await userRequests.getUser(auth?.user_id);
        const updateUser = await updateCurrentUser(updateUserObj);
        await updatePassword(getUser.password, form.password);
        setLoading(false);
        if (updateUser) {
            return history.push({ pathname: '/', state: { showWelcomeDialog: true } });
        }
    };

    const handleChange = async (key, data) => {
        switch (key) {
            case 'firstName': {
                return setForm((current) => ({ ...current, firstName: data }));
            }
            case 'lastName': {
                return setForm((current) => ({ ...current, lastName: data }));
            }
            case 'preferredName': {
                return setForm((current) => ({ ...current, preferredName: data }));
            }
            case 'password': {
                return setForm((current) => ({ ...current, password: data }));
            }
            case 'confirmPassword': {
                return setForm((current) => ({ ...current, confirmPassword: data }));
            }
            case 'pronoun': {
                return setForm((current) => ({ ...current, pronoun: data }));
            }
            case 'englishProficiency': {
                return setForm((current) => ({ ...current, englishProficiency: data }));
            }
            case 'country': {
                return setForm((current) => ({ ...current, country: data }));
            }
            case 'timezone': {
                return setForm((current) => ({ ...current, timezone: data }));
            }
            default:
                return;
        }
    };

    const handleNewPasswordRequirement = (password) => {
        const atLeastOneUpperCase = /^.*[A-Z]+.*$/;
        const atLeastOneLowerCase = /^.*[a-z]+.*$/;
        const atLeastOneNumber = /^.*[0-9]+.*$/;
        const atLeast8Character = /^[a-zA-Z0-9!"#$%&'()*+,-./:;<=>?@[\]^_`{|}~]{8,20}$/;
        const atLeastOneSpecial = /^(?=.*[!@#$%^&*]).*$/;
        const validateOneUpperCase = atLeastOneUpperCase.exec(password);
        const validateOneLowerCase = atLeastOneLowerCase.exec(password);
        const validateOneNumber = atLeastOneNumber.exec(password);
        const validateMoreThan8Characters = atLeast8Character.exec(password);
        const validateOneSpecial = atLeastOneSpecial.exec(password);
        setSatisfyRequirement({
            validateOneUpperCase: !validateOneUpperCase ? true : false,
            validateOneLowerCase: !validateOneLowerCase ? true : false,
            validateOneNumber: !validateOneNumber ? true : false,
            validateMoreThan8Characters: !validateMoreThan8Characters ? true : false,
            validateOneSpecial: !validateOneSpecial ? true : false,
        });
    };

    const getErrorMessage = (key, value, form) => {
        value = value?.trim();
        switch (key) {
            case 'firstName':
                return value ? '' : 'Please enter a first name';
            case 'lastName':
                return value ? '' : 'Please enter a last name';
            case 'password':
                if (Object.values(satisfyRequirement).find((requirement) => requirement))
                    return 'Please satisfy all requirements';
                return value ? '' : 'Please enter password';
            case 'confirmPassword':
                return form.password === value ? '' : 'Passwords do not match';
            default:
                return '';
        }
    };

    const validate = (key, value) => {
        const errorMessage = getErrorMessage(key, value, form);
        setErrors((errors) => {
            return {
                ...errors,
                [key]: errorMessage,
            };
        });
    };

    const validateAll = () => {
        return !Boolean(
            Object.keys(errors[currentStep])
                .map((key) => {
                    validate(key, form[key]);
                    return getErrorMessage(key, form[key], form);
                })
                .reduce((cur, acc) => {
                    return cur + acc;
                }, '')
        );
    };

    useEffect(() => {
        if (form?.password.length) {
            handleNewPasswordRequirement(form?.password);
        }
    }, [form?.password]);

    return (
        <PageContext.Provider
            value={{
                loading,
                form,
                errors,
                currentStep,
                satisfyRequirement,

                handleChange,
                handlePrev,
                handleNext,
                handleComplete,
                setErrors,
                setForm,
                PAGES,
            }}
            {...props}
        />
    );
};

export { PageContext };
export default PageProvider;
